import { ReactFlowProvider } from '@xyflow/react'
import { useEffect } from 'react'
import '@xyflow/react/dist/style.css'
import './style.scss'
import { Loader } from '@neo/primitives/loader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { useParams } from 'router-v6'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { NotFoundPage } from '../misc/404'
import ExternalApps from '../settings/integrations/apps/external-apps'
import { IntegrationsMigrationBanner } from './components/integrations-migration-banner'
import { TitleBar } from './components/title-bar'
import { LegacyReadOnlyBanner } from './components/title-bar/legacy-readonly-banner'
import { VibeAgentSidebar } from './components/vibe-agent/components/sidebar'
import { useAgentSettingsVisibility } from './hooks/use-agent-settings-visibility'
import { useCopyPaste } from './hooks/use-copy-paste'
import { useFlowId } from './hooks/use-flow-id'
import { useHasAINode } from './hooks/use-has-ai-node'
import { FlowCanvasContent } from './index'
import { useFlowQuery } from './queries'
import { useFlowStore } from './store'
import { AgentFlowRunsTab } from './tabs/agent-flow-runs'
import { AgentKnowledgeBaseTab } from './tabs/agent-knowledge-base'
import { AgentSecretsTab } from './tabs/agent-secrets-tab'
import { AgentSettingsTab } from './tabs/agent-settings-tab'
import { EventCallbacksTab } from './tabs/event-callbacks-tab'
import { MetricsTab } from './tabs/metrics-tab'
import { SimulationTab } from './tabs/simulation-tab'
import { VoiceConfigTab } from './tabs/voice-config-tab'
import { isLegacyReadOnly } from './utils'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

type AgentBuilderTab =
  | 'canvas'
  | 'metrics'
  | 'knowledge-base'
  | 'runs'
  | 'settings'
  | 'secrets'
  | 'external-tools'
  | 'voice-config'
  | 'event-callbacks'
  | 'simulation'

const AgentBuilderContent = () => {
  const { id } = useParams()
  const navigate = useAppNavigate()
  const { isNew } = useFlowId()
  const { data: flowData, ...queryData } = useFlowQuery(id || '', undefined, isNew)
  const { pasteContent } = useCopyPaste()
  const { showSettingsTab, showMetricsTab, showVoiceConfigTab, showEventCallbacksTab } =
    useAgentSettingsVisibility()
  const hasAINode = useHasAINode()
  const { isSimulationEvalEnabled, vibeAgentDisabled } = useFeatureFlags()
  const { isLivekitEnabled } = useLivekitEnabled()
  // Simulations run through the vibe/AI agent, so hide the tab when the vibe
  // agent is disabled (SER-6067) — not just behind its own eval flag.
  const showSimulationTab = isSimulationEvalEnabled && isLivekitEnabled && !vibeAgentDisabled

  const getCurrentTab = (): AgentBuilderTab => {
    if (location.pathname.includes('/metrics') && showMetricsTab) return 'metrics'
    if (location.pathname.includes('/knowledge-base') && hasAINode) return 'knowledge-base'
    if (location.pathname.includes('/runs')) return 'runs'
    if (location.pathname.includes('/simulation') && showSimulationTab) return 'simulation'
    if (location.pathname.includes('/settings') && showSettingsTab) return 'settings'
    if (location.pathname.includes('/voice-config') && showVoiceConfigTab) return 'voice-config'
    if (location.pathname.includes('/event-callbacks') && showEventCallbacksTab)
      return 'event-callbacks'
    if (location.pathname.includes('/secrets') && hasAINode) return 'secrets'
    if (location.pathname.includes('/external-tools') && hasAINode) return 'external-tools'
    return 'canvas'
  }

  const currentTab = getCurrentTab()
  const requestedPath = useFlowStore((s) => s.requestedPath)
  const basePath = `/agent/builder/${id}`

  const handleTabSwitch = (tab: string) => {
    const pathMap: Record<AgentBuilderTab, string> = {
      canvas: '',
      metrics: '/metrics',
      'knowledge-base': '/knowledge-base',
      runs: '/runs',
      simulation: '/simulation',
      settings: '/settings',
      'voice-config': '/voice-config',
      'event-callbacks': '/event-callbacks',
      secrets: '/secrets',
      'external-tools': '/external-tools',
    }
    navigate(`${basePath}${pathMap[tab as AgentBuilderTab]}${location.search}`, { replace: true })
  }

  useEffect(() => {
    if (requestedPath) {
      navigate(`${basePath}${requestedPath}`, { replace: true })
      useFlowStore.getState().setRequestedPath(null)
    }
  }, [requestedPath])

  if (queryData.isPending) return <Loader text="Loading agent data..." />
  if (flowData === null) return <NotFoundPage />

  return (
    <div className="h-full flex flex-col bg-[#FCFCFC] dark:bg-gray-950" data-testid="agent-builder">
      <TitleBar flowData={flowData} pasteContent={pasteContent} />

      {flowData && isLegacyReadOnly(flowData.phlo) && <LegacyReadOnlyBanner flowData={flowData} />}

      <Tabs
        value={currentTab}
        onValueChange={handleTabSwitch}
        className="flex flex-col flex-1 min-h-0 border-t-0"
      >
        <TabsList variant="simple" className="px-6 flex-shrink-0 pl-10 border-none relative z-10">
          <TabsTrigger value="canvas" variant="simple" data-testid="tab-canvas">
            Flow
          </TabsTrigger>
          {showMetricsTab && (
            <TabsTrigger value="metrics" variant="simple" data-testid="tab-metrics">
              Metrics
            </TabsTrigger>
          )}
          <TabsTrigger value="runs" variant="simple" data-testid="tab-runs">
            Agent Runs
          </TabsTrigger>
          {showSimulationTab && (
            <TabsTrigger value="simulation" variant="simple" data-testid="tab-simulation">
              Simulations
            </TabsTrigger>
          )}
          {showEventCallbacksTab && (
            <TabsTrigger value="event-callbacks" variant="simple" data-testid="tab-event-callbacks">
              Event Callbacks
            </TabsTrigger>
          )}
          {showSettingsTab && (
            <TabsTrigger value="settings" variant="simple" data-testid="tab-settings">
              Settings
            </TabsTrigger>
          )}
          {hasAINode && (
            <TabsTrigger value="knowledge-base" variant="simple" data-testid="tab-knowledge-base">
              Knowledge Base
            </TabsTrigger>
          )}
          {hasAINode && (
            <TabsTrigger value="secrets" variant="simple" data-testid="tab-secrets">
              Secrets
            </TabsTrigger>
          )}
          {hasAINode && (
            <TabsTrigger value="external-tools" variant="simple" data-testid="tab-external-tools">
              Tools
            </TabsTrigger>
          )}
          {showVoiceConfigTab && (
            <TabsTrigger value="voice-config" variant="simple" data-testid="tab-voice-config">
              Voice Configuration
            </TabsTrigger>
          )}
        </TabsList>

        <div className="flex flex-1 min-h-0 relative">
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <TabsContent
              value="canvas"
              className="flex-1 min-h-0 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
              forceMount
            >
              <FlowCanvasContent />
            </TabsContent>

            {/* Same flag as the trigger and the route resolver — the tab's
                predecessor once gated them differently, which left a clickable
                trigger over an empty panel. */}
            {showMetricsTab && (
              <TabsContent
                value="metrics"
                className="flex-1 min-h-0 p-6 h-full overflow-y-auto data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
              >
                <MetricsTab />
              </TabsContent>
            )}

            <TabsContent
              value="runs"
              className="flex-1 min-h-0 p-6 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
            >
              <AgentFlowRunsTab />
            </TabsContent>

            {showSimulationTab && (
              <TabsContent
                value="simulation"
                forceMount
                className="flex-1 min-h-0 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
              >
                <SimulationTab />
              </TabsContent>
            )}

            {showEventCallbacksTab && (
              <TabsContent
                value="event-callbacks"
                className="flex-1 min-h-0 p-6 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
                forceMount
              >
                <EventCallbacksTab />
              </TabsContent>
            )}

            {showSettingsTab && (
              <TabsContent
                value="settings"
                className="flex-1 min-h-0 p-6 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
                forceMount
              >
                <AgentSettingsTab />
              </TabsContent>
            )}

            {hasAINode && (
              <TabsContent
                value="knowledge-base"
                className="flex-1 min-h-0 p-6 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
              >
                <AgentKnowledgeBaseTab />
              </TabsContent>
            )}

            {hasAINode && (
              <TabsContent
                value="secrets"
                className="flex-1 p-6 min-h-0 data-[state=inactive]:hidden data-[state=active]:flex flex-col mx-2 border border-st2 rounded-t bg-background"
              >
                <AgentSecretsTab />
              </TabsContent>
            )}

            {hasAINode && (
              <TabsContent
                value="external-tools"
                className="flex-1 p-6 min-h-0 data-[state=inactive]:hidden mx-2 border data-[state=active]:flex flex-col border-st2 rounded-t bg-background"
              >
                <IntegrationsMigrationBanner />
                <ExternalApps />
              </TabsContent>
            )}

            {showVoiceConfigTab && (
              <TabsContent
                value="voice-config"
                className="flex-1 min-h-0 p-6 data-[state=inactive]:hidden mx-2 border border-st2 rounded-t bg-background"
                forceMount
              >
                <VoiceConfigTab />
              </TabsContent>
            )}
          </div>
          <VibeAgentSidebar />
        </div>
      </Tabs>
    </div>
  )
}

const AgentBuilder = () => (
  <ReactFlowProvider>
    <AgentBuilderContent />
  </ReactFlowProvider>
)

export default AgentBuilder
